import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2, Check, X, Gift } from 'lucide-react'
import { usePremios, useCrearPremio, useActualizarPremio, useEliminarPremio } from '../hooks/useSorteos'
import type { Premio, PremioCreatePayload } from '../types/sorteos.types'

interface Props {
  sorteoId: string
}

const emptyForm: PremioCreatePayload = {
  nombre: '',
  descripcion: '',
  imagen_url: null,
  cantidad: 1,
  orden: 0,
  activo: true,
}

export function PremiosFormCard({ sorteoId }: Props) {
  const { data: premios = [], isLoading } = usePremios(sorteoId)
  const crear = useCrearPremio()
  const actualizar = useActualizarPremio()
  const eliminar = useEliminarPremio()

  const [form, setForm] = useState<PremioCreatePayload>({ ...emptyForm })
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Premio | null>(null)

  const handleCreate = () => {
    if (!form.nombre.trim()) return
    crear.mutate(
      { sorteoId, payload: { ...form, orden: form.orden ?? premios.length } },
      {
        onSuccess: () => {
          setForm({ ...emptyForm })
          setShowForm(false)
        },
      }
    )
  }

  const startEdit = (p: Premio) => {
    setEditingId(p.id)
    setEditForm({ ...p })
  }

  const saveEdit = () => {
    if (!editForm || !editingId) return
    actualizar.mutate(
      {
        sorteoId,
        premioId: editingId,
        payload: {
          nombre: editForm.nombre,
          descripcion: editForm.descripcion,
          imagen_url: editForm.imagen_url,
          cantidad: editForm.cantidad,
          orden: editForm.orden,
          activo: editForm.activo,
        },
      },
      { onSuccess: () => setEditingId(null) }
    )
  }

  const cancelEdit = () => setEditingId(null)

  const handleDelete = (premioId: string) => {
    eliminar.mutate({ sorteoId, premioId })
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Gift className="h-4 w-4 text-teal-500" />
            Premios del Sorteo
          </CardTitle>
          <Button
            type="button"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="bg-teal-800 hover:bg-teal-900 text-white h-8 text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Agregar Premio
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Cada premio tiene una cantidad disponible. Al agotarse, el sorteo pasa automáticamente al siguiente.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Formulario nuevo premio */}
        {showForm && (
          <div className="border border-teal-500/30 rounded-lg p-4 bg-teal-500/5 space-y-3">
            <p className="text-xs font-medium text-teal-700 dark:text-teal-400">Nuevo Premio</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">Nombre del premio *</Label>
                <Input
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Pack infantil, Camiseta, etc."
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Cantidad disponible</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.cantidad}
                  onChange={e => setForm(f => ({ ...f, cantidad: parseInt(e.target.value) || 1 }))}
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Orden de entrega</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.orden}
                  onChange={e => setForm(f => ({ ...f, orden: parseInt(e.target.value) || 0 }))}
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Descripción (opcional)</Label>
                <Textarea
                  value={form.descripcion}
                  onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Descripción del premio..."
                  className="mt-1 text-sm resize-none"
                  rows={2}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">URL de imagen (opcional)</Label>
                <Input
                  value={form.imagen_url || ''}
                  onChange={e => setForm(f => ({ ...f, imagen_url: e.target.value || null }))}
                  placeholder="https://..."
                  className="mt-1 h-8 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                onClick={handleCreate}
                disabled={crear.isPending || !form.nombre.trim()}
                className="bg-teal-800 hover:bg-teal-900 text-white h-7 text-xs"
              >
                <Check className="h-3 w-3 mr-1" />
                Guardar
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setShowForm(false); setForm({ ...emptyForm }) }}
                className="h-7 text-xs border-gray-700 text-gray-700 dark:border-gray-500 dark:text-gray-300"
              >
                <X className="h-3 w-3 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Lista de premios */}
        {isLoading && (
          <div className="text-sm text-muted-foreground text-center py-4">Cargando premios...</div>
        )}

        {!isLoading && premios.length === 0 && !showForm && (
          <div className="text-sm text-muted-foreground text-center py-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            No hay premios configurados. Agrega el primero.
          </div>
        )}

        {premios.map(p => (
          <div
            key={p.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            {editingId === p.id && editForm ? (
              <div className="p-3 space-y-3 bg-gray-50 dark:bg-gray-800/50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Nombre</Label>
                    <Input
                      value={editForm.nombre}
                      onChange={e => setEditForm(f => f ? { ...f, nombre: e.target.value } : f)}
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Cantidad</Label>
                    <Input
                      type="number"
                      min={editForm.ganadores_count || 1}
                      value={editForm.cantidad}
                      onChange={e => setEditForm(f => f ? { ...f, cantidad: parseInt(e.target.value) || 1 } : f)}
                      className="mt-1 h-8 text-sm"
                    />
                    {editForm.ganadores_count > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Mínimo {editForm.ganadores_count} (ya hay {editForm.ganadores_count} ganadores)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Orden</Label>
                    <Input
                      type="number"
                      min={0}
                      value={editForm.orden}
                      onChange={e => setEditForm(f => f ? { ...f, orden: parseInt(e.target.value) || 0 } : f)}
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Descripción</Label>
                    <Textarea
                      value={editForm.descripcion}
                      onChange={e => setEditForm(f => f ? { ...f, descripcion: e.target.value } : f)}
                      className="mt-1 text-sm resize-none"
                      rows={2}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">URL imagen</Label>
                    <Input
                      value={editForm.imagen_url || ''}
                      onChange={e => setEditForm(f => f ? { ...f, imagen_url: e.target.value || null } : f)}
                      className="mt-1 h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={saveEdit}
                    disabled={actualizar.isPending}
                    className="bg-teal-800 hover:bg-teal-900 text-white h-7 text-xs"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Guardar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={cancelEdit}
                    className="h-7 text-xs border-gray-700 text-gray-700 dark:border-gray-500 dark:text-gray-300"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 flex items-start gap-3 group">
                {p.imagen_url && (
                  <img
                    src={p.imagen_url}
                    alt={p.nombre}
                    className="h-12 w-12 rounded object-contain flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-black dark:text-white truncate">{p.nombre}</span>
                    <Badge className="badge-gradient text-xs">{p.cantidad} unid.</Badge>
                    {p.agotado && (
                      <Badge variant="outline" className="text-xs border-gray-400 text-gray-500">Agotado</Badge>
                    )}
                  </div>
                  {p.descripcion && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.descripcion}</p>
                  )}
                  <div className="mt-1.5 space-y-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Entregados</span>
                      <span>{p.ganadores_count} / {p.cantidad}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-teal-500/10">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          background: 'linear-gradient(90deg, #14b8a6, #0f766e)',
                          width: `${Math.min(100, p.cantidad > 0 ? (p.ganadores_count / p.cantidad) * 100 : 0)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => startEdit(p)}
                    className="h-7 w-7 text-gray-500 hover:text-teal-700"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(p.id)}
                    disabled={eliminar.isPending}
                    className="h-7 w-7 text-gray-500 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
